import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { StorageKeys, StatusEnum, StoreCollection } from "src/app/enums";
import { withDevtools, withStorageSync } from "@angular-architects/ngrx-toolkit";
import { Role } from "src/app/store/admin-signal/interfaces/role.interface";
import { Credential } from "src/app/store/admin-signal/interfaces/credential.interface";
import { withLoading } from "src/app/store/features/loading.feature";
import { PaginationStoreState, withPagination } from "src/app/store/features/pagination.feature";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import {
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { catchError } from "rxjs/operators";
import { computed, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { FromProgram, FromRouter, FromUser, RootState, UserActions } from "src/app/store";
import { tapResponse } from "@ngrx/operators";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { AppRoutes, DEFAULT_PAGINATION_SIZE, KEYCLOAK_RESET_PASSWORD_LIFESPAN, KEYCLOAK_SET_PASSWORD_LIFESPAN } from "src/app/constants";
import { ConvercusAdmin } from "src/app/interfaces";
import { environment } from "src/environments/environment";
import { errorResponse } from "src/app/interfaces/errorResponse";
import { AppStore } from "src/app/store/app-signal/app.store";
import { SearchState, withSearch } from "src/app/store/features/search.feature";
import { withEntities, setAllEntities, removeEntity, setEntity } from "@ngrx/signals/entities";
import { castStatus } from "src/app/utils/cast-status-enum";
import { initialState, adminsEnabledConfig, adminRolesConfig, adminsDisabledConfig } from "./utils/initial-state";
import { parseErrorMessage } from "src/app/store/admin-signal/utils/parse-error-message";
import { getInviteErrorKey } from "src/app/store/admin-signal/utils/invite-error-message";
import { getServerConnectionErrorKey } from "src/app/store/admin-signal/utils/server-connection-error-message";
import {
  buildClientCredentialsCurl,
  ClientCredentialsCurl,
  maskClientSecret,
} from "src/app/store/admin-signal/utils/build-client-credentials-curl";
import { isM2mUser } from "src/app/store/admin-signal/utils/is-m2m-user";
import { isKeycloakAuthProfile } from "src/app/utils/program-auth";
import { preserveAdminListEnrichment } from "src/app/store/admin-signal/utils/preserve-admin-list-enrichment";
import { toKeycloakUserUpdateBody } from "src/app/utils/to-keycloak-user-update-body";
import * as Sentry from "@sentry/angular";
import { selectedProgramId } from "src/app/utils/selected-program-id";
import {
  ClientDetailResponseDto,
  ClientRepresentationDto,
  CreateClientDto,
  CreateClientResponseDto,
  InviteByEmailDto,
  KeycloakService,
  MemberRepresentationDto,
  UpdateClientDto,
} from "@api/user-app-service";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationComponent } from "src/app/modals/confirmation/confirmation.component";
import { ConfirmationDialogData } from "src/app/modals/confirmation/confirmation-dialog.interface";
import { SelectItem } from "src/app/interfaces/select-item.interface";
import { SideMenuOrganization } from "src/app/store/user/user.state";
import { AdminListItem } from "./interfaces/admin-list-item.interface";
import { ServerConnectionListItem } from "./interfaces/server-connection-list-item.interface";
import { Session } from "src/app/store/admin-signal/interfaces/session.interface";
import { KeycloakTokenRefreshService } from "src/app/services/keycloak-token-refresh.service";
import { getPartnerIdFromAttributes } from "src/app/utils/get-partner-id-from-attributes";

type AdminStoreCollection = StoreCollection.Admins | StoreCollection.ServerConnections;

const paginationInitialState: PaginationStoreState<AdminStoreCollection> = {
  paginations: {
    [StoreCollection.Admins]: { pageSize: DEFAULT_PAGINATION_SIZE, pageCount: 0, page: 1, total: 0 },
    [StoreCollection.ServerConnections]: { pageSize: DEFAULT_PAGINATION_SIZE, pageCount: 0, page: 1, total: 0 },
  },
};

const searchInitialState: SearchState<AdminStoreCollection> = {
  searchTerms: {
    [StoreCollection.Admins]: "",
    [StoreCollection.ServerConnections]: "",
  },
};

const hasOffsetIndex = false;
export const AdminStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withLoading(),
  withPagination(paginationInitialState, hasOffsetIndex),
  withSearch(searchInitialState, hasOffsetIndex),
  withEntities(adminsEnabledConfig),
  withEntities(adminsDisabledConfig),
  withEntities(adminRolesConfig),
  withProps(() => {
    const http = inject(HttpClient);
    return {
      _globalStore: inject(Store<RootState>),
      _appStore: inject(AppStore),
      _http: http,
      _keycloakService: inject(KeycloakService),
      _router: inject(Router),
      _dialog: inject(MatDialog),
      _keycloakTokenRefreshService: inject(KeycloakTokenRefreshService),
    };
  }),
  withComputed(store => ({
    _keycloakClientId: computed(() => store._globalStore.selectSignal(FromProgram.selectKeycloakClientId)() as string),
    adminId: computed(() => store._globalStore.selectSignal(FromRouter.selectAdminId)()),
    connectionId: computed(() => store._globalStore.selectSignal(FromRouter.selectConnectionId)()),
    programId: computed(() => store._globalStore.selectSignal(FromRouter.selectProgramId)()),
    currentStatus: computed(() => castStatus(store._globalStore.selectSignal(FromRouter.selectCurrentRoute)())),
  })),
  withComputed(store => ({
    isActive: computed(() => store.currentStatus() === StatusEnum.Active),
    isMultiOrganizationProgram: computed(() => store._globalStore.selectSignal(FromProgram.selectIsMultiOrganizationProgram)() ?? false),
    selectedAdmin: computed<MemberRepresentationDto | undefined>(() => {
      const adminId = store.adminId();
      if (!adminId) return undefined;
      return store.adminsEnabledEntityMap()[adminId] || store.adminsDisabledEntityMap()[adminId];
    }),
    selectedAdminRoles: computed<Role[] | undefined>(() => store.adminRolesEntities() ?? undefined),
    availableRolesSelectItems: computed(() => store.availableRoles().map(role => ({ id: role.id, name: role.name }))),
    currentOrganization: computed((): SideMenuOrganization | undefined => {
      const programId = store.programId();
      if (!programId) return undefined;
      const organizations = store._globalStore.selectSignal(FromUser.selectUserOrganizations)();
      return organizations.find(organization => organization.id === programId);
    }),
    currentOrganizationSelectItems: computed((): SelectItem[] => {
      const programId = store.programId();
      if (!programId) return [];

      const organizations = store._globalStore.selectSignal(FromUser.selectUserOrganizations)();
      const currentOrganization = organizations.find(organization => organization.id === programId);
      if (currentOrganization?.keycloakId) {
        return [
          {
            id: currentOrganization.keycloakId,
            name: currentOrganization.name,
            imageUrl: currentOrganization.imageUrl,
          },
        ];
      }

      const inviteOrganization = store.inviteOrganizationItems().find(organization => organization.name === programId);
      if (inviteOrganization?.id) {
        return [{ id: inviteOrganization.id, name: inviteOrganization.name }];
      }

      return [];
    }),
  })),
  withComputed(store => ({
    query: computed(() => ({
      searchTerm: store.searchTerms[StoreCollection.Admins](),
      first: (store.paginations[StoreCollection.Admins].page() * store.paginations[StoreCollection.Admins].pageSize()).toString(),
      max: store.paginations[StoreCollection.Admins].pageSize().toString(),
      enabled: store.isActive().toString(),
      isMultiOrganizationProgram: store.isMultiOrganizationProgram(),
    })),
    serverConnectionLoadParams: computed(() => {
      const connectionId = store.connectionId();
      const clientInternalIdFromQuery = store._globalStore.selectSignal(FromRouter.selectClientInternalId)() as string | undefined;
      const clientInternalIdFromList = connectionId
        ? store.serverConnections().find(connection => connection.id === connectionId)?.clientInternalId
        : undefined;

      return {
        connectionId,
        isMultiOrganizationProgram: store.isMultiOrganizationProgram(),
        clientInternalId:
          store.serverConnectionClientInternalIdHint() ??
          clientInternalIdFromQuery ??
          clientInternalIdFromList ??
          connectionId ??
          store.selectedServerConnectionInternalId() ??
          null,
      };
    }),
    isServerConnectionDetailReady: computed(() => {
      const connectionId = store.connectionId();
      return !!connectionId && store.loadedServerConnectionId() === connectionId && !!store.selectedServerConnectionDetail();
    }),
    serverConnectionClientId: computed(
      () => store.selectedServerConnectionDetail()?.client?.clientId ?? store.selectedServerConnectionDetail()?.clientName ?? "",
    ),
    serverConnectionName: computed(() => store.selectedServerConnectionDetail()?.client?.name ?? ""),
    serverConnectionDescription: computed(() => store.selectedServerConnectionDetail()?.client?.description ?? ""),
    serverConnectionOrganization: computed(() => {
      const org = store.selectedServerConnectionDetail()?.organizations?.[0];
      return org?.alias ?? org?.name ?? org?.id ?? "";
    }),
    serverConnectionRole: computed(() => store.selectedServerConnectionDetail()?.groups?.[0]?.name ?? ""),
    serverConnectionRoleId: computed(() => store.selectedServerConnectionDetail()?.groups?.[0]?.id ?? ""),
    serverConnectionSecret: computed(() => store.pendingNewClientSecret() ?? store.selectedServerConnectionDetail()?.clientSecret ?? ""),
    serverConnectionMaskedSecret: computed(() => {
      const secret = store.pendingNewClientSecret() ?? store.selectedServerConnectionDetail()?.clientSecret ?? "";
      return maskClientSecret(secret);
    }),
    serverConnectionTokenCurl: computed((): ClientCredentialsCurl | null => {
      const keycloakUrl = store._globalStore.selectSignal(FromProgram.selectKeycloakUrl)() as string | undefined;
      const keycloakRealm = store._globalStore.selectSignal(FromProgram.selectKeycloakRealm)() as string | undefined;
      const clientId = store.selectedServerConnectionDetail()?.client?.clientId ?? store.selectedServerConnectionDetail()?.clientName ?? "";
      const clientSecret = store.pendingNewClientSecret() ?? store.selectedServerConnectionDetail()?.clientSecret ?? "";
      if (!keycloakUrl || !keycloakRealm || !clientId || !clientSecret) return null;
      const tokenUrl = `${keycloakUrl}/realms/${keycloakRealm}/protocol/openid-connect/token`;
      return buildClientCredentialsCurl({
        tokenUrl,
        clientId,
        clientSecret,
        displayClientSecret: maskClientSecret(clientSecret),
      });
    }),
    serverConnectionsQuery: computed(() => ({
      searchTerm: store.searchTerms[StoreCollection.ServerConnections](),
    })),
    filteredServerConnections: computed(() => {
      const searchTerm = store.searchTerms[StoreCollection.ServerConnections]().trim().toLowerCase();
      const connections = store.serverConnections();
      if (!searchTerm) return connections;
      return connections.filter(connection => {
        const haystack = [connection.name, connection.clientId, connection.description].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(searchTerm);
      });
    }),
    paginatedServerConnections: computed(() => {
      const searchTerm = store.searchTerms[StoreCollection.ServerConnections]().trim().toLowerCase();
      const connections = store.serverConnections();
      const filtered = !searchTerm
        ? connections
        : connections.filter(connection => {
            const haystack = [connection.name, connection.clientId, connection.description].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(searchTerm);
          });
      const pagination = store.paginations()[StoreCollection.ServerConnections] ?? {
        pageSize: DEFAULT_PAGINATION_SIZE,
        pageCount: 0,
        page: 0,
        total: 0,
      };
      const start = pagination.page * pagination.pageSize;
      return filtered.slice(start, start + pagination.pageSize);
    }),
  })),
  withMethods(store => ({
    setFormError(error: string): void {
      patchState(store, { formError: error });
    },
    setLastListPath(path: string): void {
      patchState(store, { lastListPath: path.toLowerCase() });
    },
    _getAdmin: rxMethod<string>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(adminId =>
          store._http.get<ConvercusAdmin>(`${environment.baseApi}/programs/${store.programId()}/admins/${adminId}`).pipe(
            tapResponse({
              next: ({ id, userName }) => {
                const admin: MemberRepresentationDto = { id, username: userName };
                const existingAdmin = store.adminsEnabledEntityMap()[id] ?? store.adminsDisabledEntityMap()[id];
                // to be define where to set the admin enabled or disabled?
                patchState(store, setEntity(preserveAdminListEnrichment(admin, existingAdmin), adminsEnabledConfig), {
                  formError: "",
                  keycloakAdminLoaded: true,
                });
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    _updateAdminBasicInfo(admin: MemberRepresentationDto): Observable<void> {
      const id = admin.id;
      if (id == null) return of(undefined);
      return store._keycloakService.keycloakControllerUpdateUser({
        userId: id,
        body: toKeycloakUserUpdateBody(admin),
      }) as Observable<void>;
    },
    _updateAdminPermissions(updatedRoles: Role[]): Observable<void> {
      const rolesToAdd = updatedRoles.filter(role => !store.selectedAdminRoles()?.some(prevRole => prevRole.id === role.id));
      const rolesToRemove = store.selectedAdminRoles()?.filter(role => !updatedRoles.some(adminRole => adminRole.id === role.id));

      if (!rolesToAdd.length && !rolesToRemove?.length) {
        return of(undefined);
      }

      const addRoleUpdates$: Observable<unknown>[] = rolesToAdd.map(role =>
        store._keycloakService.keycloakControllerAddUserToGroup({
          userId: store.adminId(),
          groupId: role.id,
        }),
      );

      const removeRoleUpdates$: Observable<unknown>[] = rolesToRemove
        ? rolesToRemove.map(role =>
            store._keycloakService.keycloakControllerRemoveUserFromGroup({
              userId: store.adminId(),
              groupId: role.id,
            }),
          )
        : [];

      return forkJoin([...addRoleUpdates$, ...removeRoleUpdates$]).pipe(switchMap(() => of(undefined)));
    },
    _getM2mClientIds(): Observable<Set<string>> {
      return store._keycloakService.keycloakControllerGetOrganizationClients().pipe(
        map(
          (clients: ClientRepresentationDto[]) =>
            new Set((clients ?? []).map(client => client.clientId).filter((clientId): clientId is string => !!clientId)),
        ),
        catchError(() => of(new Set<string>())),
      );
    },
    _mapServerConnectionListItem(client: ClientRepresentationDto): ServerConnectionListItem | null {
      const clientInternalId = client.id;
      if (!clientInternalId) return null;

      return {
        id: clientInternalId,
        clientInternalId,
        name: client.name ?? client.clientId ?? "",
        description: client.description,
        clientId: client.clientId ?? "",
      };
    },
  })),
  withMethods(store => ({
    getAdminsCount: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        switchMap(({ searchTerm, enabled }) =>
          store._keycloakService
            .keycloakControllerGetUsersCount({
              search: searchTerm ? `*${searchTerm}*` : "",
              enabled,
            })
            .pipe(
              tapResponse({
                next: (total: number) => {
                  store.setPagination({ total }, StoreCollection.Admins);
                },
                error: err => {
                  Sentry.captureException(err);
                },
              }),
            ),
        ),
      ),
    ),
    getAdmins: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(({ searchTerm, first, max, enabled }) =>
          forkJoin({
            admins: store._keycloakService.keycloakControllerGetUsers({
              first,
              max,
              search: searchTerm ? searchTerm : "",
              enabled,
            }),
            m2mClientIds: store.isMultiOrganizationProgram() ? store._getM2mClientIds() : of(new Set<string>()),
          }).pipe(
            map(({ admins, m2mClientIds }) =>
              admins.map(
                (admin): AdminListItem => ({
                  ...admin,
                  isM2mUser: store.isMultiOrganizationProgram() ? isM2mUser(admin, m2mClientIds) : false,
                }),
              ),
            ),
            tapResponse({
              next: (admins: AdminListItem[]) => {
                const humanAdmins = store.isMultiOrganizationProgram() ? admins.filter(admin => !admin.isM2mUser) : admins;
                if (enabled === "true") {
                  patchState(store, setAllEntities(humanAdmins, adminsEnabledConfig));
                } else {
                  patchState(store, setAllEntities(humanAdmins, adminsDisabledConfig));
                }
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    deleteAdmin: rxMethod<{ admin: MemberRepresentationDto; shouldNavigate: boolean }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ admin, shouldNavigate }) => {
          const userId = admin.id;
          if (userId == null) return EMPTY;
          return store._keycloakService.keycloakControllerDeleteUser({ userId }).pipe(
            tapResponse({
              next: () => {
                const config = admin.enabled ? adminsEnabledConfig : adminsDisabledConfig;
                patchState(store, removeEntity(userId, config), {
                  paginations: {
                    ...store.paginations(),
                    [StoreCollection.Admins]: {
                      ...store.paginations()[StoreCollection.Admins],
                      total: store.paginations()[StoreCollection.Admins].total - 1,
                    },
                  },
                  formError: "",
                });
                const programId = store._globalStore.selectSignal(FromRouter.selectProgramId)();
                if (shouldNavigate) void store._router.navigate([AppRoutes.root, programId, AppRoutes.admins]);
                const currentUserId = store._globalStore.selectSignal(FromUser.selectUserId)();
                if (admin.id === currentUserId) {
                  store._globalStore.dispatch(UserActions.keycloakLogout());
                }
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),
    getAvailableRoles: rxMethod<void>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(() =>
          store._keycloakService.keycloakControllerGetGroups().pipe(
            tapResponse({
              next: (roles: Role[]) => {
                patchState(store, { availableRoles: roles });
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    getKeycloakAdmin: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { keycloakAdminLoaded: false })),
        tap(() => store.setLoading(true)),
        switchMap(adminId =>
          store._keycloakService.keycloakControllerGetUser({ userId: adminId }).pipe(
            tapResponse({
              next: (admin: MemberRepresentationDto) => {
                const config = admin.enabled ? adminsEnabledConfig : adminsDisabledConfig;
                const existingAdmin = store.adminsEnabledEntityMap()[admin.id] ?? store.adminsDisabledEntityMap()[admin.id];
                patchState(store, setEntity(preserveAdminListEnrichment(admin, existingAdmin), config), {
                  formError: "",
                  keycloakAdminLoaded: true,
                });
              },
              error: err => {
                Sentry.captureException(err);
                patchState(store, { keycloakAdminLoaded: false });
                store._getAdmin(adminId);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    getAdminRoles: rxMethod<string>(
      pipe(
        filter(() => (store.adminId()?.length ?? 0) > 0),
        tap(() => store.setLoading(true)),
        switchMap(adminId =>
          store._keycloakService.keycloakControllerGetUserGroups({ userId: adminId }).pipe(
            tapResponse({
              next: (roles: Role[]) => {
                patchState(store, setAllEntities(roles, adminRolesConfig));
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    getAdminSessions: rxMethod<string>(
      pipe(
        filter(() => (store.adminId()?.length ?? 0) > 0),
        tap(() => store.setLoading(true)),
        switchMap(adminId =>
          store._keycloakService.keycloakControllerGetUserSessions({ userId: adminId }).pipe(
            tapResponse({
              next: (sessions: Session[]) => {
                patchState(store, { adminLastSession: sessions[0]?.lastAccess?.toString() ?? undefined });
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    getAdminCredentials: rxMethod<string>(
      pipe(
        filter(() => (store.adminId()?.length ?? 0) > 0),
        tap(() => store.setLoading(true)),
        switchMap(adminId =>
          store._keycloakService.keycloakControllerGetUserCredentials({ userId: adminId }).pipe(
            tapResponse({
              next: (credentials: Credential[]) => {
                patchState(store, { adminCredentials: credentials });
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    sendResetPasswordEmail: rxMethod<void>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(() =>
          store._keycloakService
            .keycloakControllerExecuteActionsEmail({
              userId: store.adminId(),
              requestBody: ["UPDATE_PASSWORD"],
              redirectUri: `${store._appStore.origin()}/${store.programId()}/login`,
              clientId: store._keycloakClientId(),
              lifespan: KEYCLOAK_RESET_PASSWORD_LIFESPAN.toString(),
            })
            .pipe(
              tapResponse({
                next: () => EMPTY,
                error: err => {
                  const errorMessage = parseErrorMessage(err as errorResponse);
                  Sentry.captureException(err);
                  store.setFormError(errorMessage);
                },
                finalize: () => store.setLoading(false),
              }),
            ),
        ),
      ),
    ),
    updateAdminBasicInfo: rxMethod<{ admin: MemberRepresentationDto; callback?: () => void }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ admin, callback }) =>
          store._updateAdminBasicInfo(admin).pipe(
            tapResponse({
              next: () => {
                const adminId = admin.id;
                if (adminId == null) return;
                const config = admin.enabled ? adminsEnabledConfig : adminsDisabledConfig;
                const delConfig = admin.enabled ? adminsDisabledConfig : adminsEnabledConfig;
                const existingAdmin = store.adminsEnabledEntityMap()[adminId] ?? store.adminsDisabledEntityMap()[adminId];
                patchState(store, setEntity(preserveAdminListEnrichment(admin, existingAdmin), config), removeEntity(adminId, delConfig), {
                  formError: "",
                });
                callback?.();
              },
              error: (err: errorResponse) => {
                const errorMessage = parseErrorMessage(err);
                Sentry.captureException(err);
                if (errorMessage === "readOnlyAttribute") {
                  store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                    data: {
                      title: "errorMessages.errorPageTitle",
                      errorMessage: "errorMessages.readOnlyAttribute",
                      closeText: "buttons.close",
                    },
                  });
                  patchState(store, { formError: "" });
                } else {
                  patchState(store, { formError: errorMessage });
                }
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    updateAdminComplete: rxMethod<{ admin: MemberRepresentationDto; updatedRoles: Role[]; callback?: () => void }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ admin, updatedRoles, callback }) => {
          const currentUserId = store._globalStore.selectSignal(FromUser.selectUserId)();
          const previousAdmin = store.selectedAdmin();
          const partnerIdChanged = getPartnerIdFromAttributes(admin.attributes) !== getPartnerIdFromAttributes(previousAdmin?.attributes);
          const shouldRefreshSession = !!admin.id && admin.id === currentUserId && partnerIdChanged;

          return forkJoin([store._updateAdminBasicInfo(admin), store._updateAdminPermissions(updatedRoles)]).pipe(
            switchMap(() => {
              if (!shouldRefreshSession) {
                return of(admin);
              }

              return store._keycloakTokenRefreshService.refreshCurrentSession({ logoutOnFailure: false }).pipe(
                switchMap(() => store._keycloakService.keycloakControllerGetUser({ userId: admin.id })),
                map(freshAdmin => ({ ...admin, ...freshAdmin })),
                tap(freshAdmin => store._globalStore.dispatch(UserActions.getUserDataSuccess({ user: freshAdmin }))),
                catchError(error => {
                  Sentry.captureException(error);
                  return of(admin);
                }),
              );
            }),
            tapResponse({
              next: savedAdmin => {
                const adminId = savedAdmin.id;
                if (adminId == null) return;
                const config = savedAdmin.enabled ? adminsEnabledConfig : adminsDisabledConfig;
                const delConfig = savedAdmin.enabled ? adminsDisabledConfig : adminsEnabledConfig;
                const existingAdmin = store.adminsEnabledEntityMap()[adminId] ?? store.adminsDisabledEntityMap()[adminId];
                patchState(
                  store,
                  setEntity(preserveAdminListEnrichment(savedAdmin, existingAdmin, updatedRoles), config),
                  removeEntity(adminId, delConfig),
                  setAllEntities(updatedRoles, adminRolesConfig),
                  {
                    formError: "",
                  },
                );
                callback?.();
              },
              error: (err: errorResponse) => {
                const errorMessage = parseErrorMessage(err);
                Sentry.captureException(err);
                if (errorMessage === "readOnlyAttribute") {
                  store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                    data: {
                      title: "errorMessages.errorPageTitle",
                      errorMessage: "errorMessages.readOnlyAttribute",
                      closeText: "buttons.close",
                    },
                  });
                  patchState(store, { formError: "" });
                } else {
                  patchState(store, { formError: errorMessage });
                }
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),
    createAdmin: rxMethod<{ newAdmin: Omit<MemberRepresentationDto, "id">; adminRoles: Role[] }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ newAdmin, adminRoles }) =>
          store._keycloakService
            .keycloakControllerCreateUser({
              body: { ...newAdmin, requiredActions: ["UPDATE_PASSWORD"] },
              redirectUri: `${store._appStore.origin()}/${store.programId()}/login`,
              clientId: store._keycloakClientId(),
              lifespan: KEYCLOAK_SET_PASSWORD_LIFESPAN.toString(),
            })
            .pipe(
              mergeMap((response: { id?: string }) => {
                const adminId = response.id;
                if (!adminId) {
                  return of(undefined).pipe(
                    tapResponse({
                      next: () => EMPTY,
                      error: (err: errorResponse) => {
                        Sentry.captureException(err);
                        store.setFormError(parseErrorMessage(err));
                      },
                      finalize: () => store.setLoading(false),
                    }),
                  );
                }
                const admin: MemberRepresentationDto = { ...newAdmin, id: adminId };

                const adminRoleUpdates$: Observable<unknown>[] = adminRoles.map(adminRole =>
                  store._keycloakService.keycloakControllerAddUserToGroup({
                    userId: adminId,
                    groupId: adminRole.id,
                  }),
                );

                return forkJoin(adminRoleUpdates$).pipe(
                  tapResponse({
                    next: () => {
                      patchState(store, setEntity(admin, adminsEnabledConfig), setAllEntities(adminRoles, adminRolesConfig), {
                        formError: "",
                      });
                    },
                    error: (err: errorResponse) => {
                      const errorMessage = parseErrorMessage(err);
                      Sentry.captureException(err);
                      store.setFormError(errorMessage);
                    },
                    complete: () => {
                      void store._router.navigate([AppRoutes.root, store.programId(), AppRoutes.admins, adminId]);
                    },
                  }),
                );
              }),
              tapResponse({
                next: () => EMPTY,
                error: (err: errorResponse) => {
                  Sentry.captureException(err);
                  const errorMessage = parseErrorMessage(err);
                  store.setFormError(errorMessage);
                },
                finalize: () => store.setLoading(false),
              }),
            ),
        ),
      ),
    ),
    loadInviteOrganizations: rxMethod<number>(
      pipe(
        filter(trigger => trigger > 0),
        exhaustMap(() => {
          const userId = store._globalStore.selectSignal(FromUser.selectUserId)();
          const isMultiOrganization = store._globalStore.selectSignal(FromProgram.selectIsMultiOrganizationProgram)();
          if (!userId) {
            store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
              data: {
                title: "errorMessages.errorPageTitle",
                errorMessage: "admins.invite.errors.userContextRequired",
                closeText: "buttons.close",
              },
            });
            return EMPTY;
          }
          if (!isMultiOrganization) {
            patchState(store, { inviteOrganizationItems: [], inviteLoading: false });
            return EMPTY;
          }
          patchState(store, { inviteLoading: true });
          return store._keycloakService.keycloakControllerGetUserOrganizations({ userId }).pipe(
            tapResponse({
              next: orgs => {
                const items = (orgs ?? [])
                  .map(org => ({
                    id: org.id ?? "",
                    name: org.name ?? org.alias ?? org.id ?? "",
                  }))
                  .filter((o): o is { id: string; name: string } => !!o.id);
                patchState(store, { inviteOrganizationItems: items, inviteLoading: false });
              },
              error: err => {
                Sentry.captureException(err);
                store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                  data: {
                    title: "errorMessages.errorPageTitle",
                    errorMessage: getInviteErrorKey(err, "loadFailed"),
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => patchState(store, { inviteLoading: false }),
            }),
          );
        }),
      ),
    ),
    ensureCurrentOrganization(): void {
      if (!store.isMultiOrganizationProgram() || store.currentOrganizationSelectItems().length > 0) {
        return;
      }

      const userId = store._globalStore.selectSignal(FromUser.selectUserId)();
      if (userId) {
        store._globalStore.dispatch(UserActions.getUserOrganizations({ userId }));
      }
    },
    setServerConnectionsLoading(loading: boolean): void {
      patchState(store, { serverConnectionsLoading: loading });
    },
    getServerConnections: rxMethod<void>(
      pipe(
        filter(() => !store.serverConnectionsLoading() && !store.serverConnectionsLoaded()),
        tap(() => patchState(store, { serverConnectionsLoading: true })),
        switchMap(() =>
          store._keycloakService.keycloakControllerGetOrganizationClients().pipe(
            map((clients: ClientRepresentationDto[]) =>
              clients.map(client => store._mapServerConnectionListItem(client)).filter((item): item is ServerConnectionListItem => !!item),
            ),
            tapResponse({
              next: (connections: ServerConnectionListItem[]) => {
                patchState(store, { serverConnections: connections, serverConnectionsLoaded: true });
                store.setPagination({ total: connections.length, page: 0 }, StoreCollection.ServerConnections);
              },
              error: err => {
                Sentry.captureException(err);
                patchState(store, { serverConnections: [], serverConnectionsLoaded: false });
              },
              finalize: () => patchState(store, { serverConnectionsLoading: false }),
            }),
            catchError(() => {
              patchState(store, { serverConnections: [], serverConnectionsLoaded: false });
              return EMPTY;
            }),
          ),
        ),
      ),
    ),
    clearSelectedServerConnection(): void {
      patchState(store, {
        selectedServerConnectionInternalId: null,
        selectedServerConnectionDetail: null,
        loadedServerConnectionId: null,
        serverConnectionClientInternalIdHint: null,
      });
    },
    setServerConnectionClientInternalIdHint(clientInternalId: string | null): void {
      patchState(store, { serverConnectionClientInternalIdHint: clientInternalId });
    },
    loadServerConnection: rxMethod<ReturnType<typeof store.serverConnectionLoadParams>>(
      pipe(
        distinctUntilChanged((previous, current) => {
          if (
            previous.connectionId !== current.connectionId ||
            previous.clientInternalId !== current.clientInternalId ||
            previous.isMultiOrganizationProgram !== current.isMultiOrganizationProgram
          ) {
            return false;
          }

          if (!current.connectionId) {
            return true;
          }

          return (
            store.loadedServerConnectionId() === current.connectionId &&
            !!store.selectedServerConnectionDetail() &&
            (current.clientInternalId == null || store.selectedServerConnectionInternalId() === current.clientInternalId)
          );
        }),
        switchMap(({ connectionId, isMultiOrganizationProgram, clientInternalId }) => {
          if (!connectionId) {
            patchState(store, {
              selectedServerConnectionInternalId: null,
              selectedServerConnectionDetail: null,
              loadedServerConnectionId: null,
            });
            return EMPTY;
          }

          const config = store._globalStore.selectSignal(FromProgram.selectConfig)();
          const realmInfo = store._globalStore.selectSignal(FromProgram.selectKeycloakRealmInfo)();
          const isKeycloakProgram = isKeycloakAuthProfile(config);

          if (isKeycloakProgram && realmInfo === null) {
            return EMPTY;
          }

          if (!isMultiOrganizationProgram) {
            patchState(store, {
              selectedServerConnectionInternalId: null,
              selectedServerConnectionDetail: null,
              loadedServerConnectionId: null,
            });
            return EMPTY;
          }

          if (
            store.loadedServerConnectionId() === connectionId &&
            store.selectedServerConnectionDetail() &&
            (clientInternalId == null || store.selectedServerConnectionInternalId() === clientInternalId)
          ) {
            return EMPTY;
          }

          patchState(store, {
            selectedServerConnectionInternalId: null,
            selectedServerConnectionDetail: null,
            loadedServerConnectionId: null,
          });
          store.setLoading(true);

          const applyDetailResult = (result: { detail: ClientDetailResponseDto; clientInternalId: string } | null): void => {
            if (store.connectionId() !== connectionId) {
              return;
            }

            if (!result) {
              patchState(store, {
                selectedServerConnectionInternalId: null,
                selectedServerConnectionDetail: null,
                loadedServerConnectionId: null,
                serverConnectionClientInternalIdHint: null,
                formError: "",
              });
              return;
            }

            patchState(store, {
              selectedServerConnectionInternalId: result.clientInternalId,
              selectedServerConnectionDetail: result.detail,
              loadedServerConnectionId: connectionId,
              serverConnectionClientInternalIdHint: null,
              formError: "",
            });
          };

          const includeSecretParam = (includeSecret: boolean): string => (includeSecret ? "true" : "false");

          const fetchServerConnectionDetail = (
            resolvedClientInternalId: string | null,
            includeSecret: boolean,
          ): Observable<{ detail: ClientDetailResponseDto; clientInternalId: string } | null> => {
            const includeSecretValue = includeSecretParam(includeSecret);

            if (resolvedClientInternalId) {
              return store._keycloakService
                .keycloakControllerGetServiceAccountClientDetail({
                  clientInternalId: resolvedClientInternalId,
                  includeSecret: includeSecretValue,
                })
                .pipe(
                  map(detail => ({ detail, clientInternalId: resolvedClientInternalId })),
                  catchError((err: HttpErrorResponse) => {
                    if (err.status !== 404) {
                      return throwError(() => err);
                    }

                    return store._keycloakService
                      .keycloakControllerGetServiceAccountClientDetailByServiceAccountUserId({
                        serviceAccountUserId: connectionId,
                        includeSecret: includeSecretValue,
                      })
                      .pipe(
                        map(detail => {
                          const resolvedId = detail.client?.id;
                          if (!resolvedId) return null;
                          return { detail, clientInternalId: resolvedId };
                        }),
                      );
                  }),
                );
            }

            return store._keycloakService
              .keycloakControllerGetServiceAccountClientDetailByServiceAccountUserId({
                serviceAccountUserId: connectionId,
                includeSecret: includeSecretValue,
              })
              .pipe(
                map(detail => {
                  const resolvedId = detail.client?.id;
                  if (!resolvedId) return null;
                  return { detail, clientInternalId: resolvedId };
                }),
              );
          };

          const patchServerConnectionSecret = (clientSecret: string | undefined): void => {
            if (store.connectionId() !== connectionId || !clientSecret) {
              return;
            }

            const current = store.selectedServerConnectionDetail();
            if (!current) {
              return;
            }

            patchState(store, {
              selectedServerConnectionDetail: {
                ...current,
                clientSecret,
              },
            });
          };

          const loadServerConnectionSecret = (resolvedClientInternalId: string): Observable<void> =>
            store._keycloakService.keycloakControllerGetServiceAccountClientSecret({ clientInternalId: resolvedClientInternalId }).pipe(
              tap(response => patchServerConnectionSecret(response.clientSecret)),
              map(() => undefined),
              catchError((err: HttpErrorResponse) => {
                if (err.status !== 405 && err.status !== 404) {
                  Sentry.captureException(err);
                  return EMPTY;
                }

                return fetchServerConnectionDetail(resolvedClientInternalId, true).pipe(
                  tap(secretResult => patchServerConnectionSecret(secretResult?.detail.clientSecret)),
                  map(() => undefined),
                  catchError(fallbackErr => {
                    Sentry.captureException(fallbackErr);
                    return EMPTY;
                  }),
                );
              }),
            );

          return fetchServerConnectionDetail(clientInternalId, false).pipe(
            switchMap(result => {
              if (store.connectionId() !== connectionId) {
                return EMPTY;
              }

              applyDetailResult(result);
              store.setLoading(false);

              if (!result || store.pendingNewClientSecret()) {
                return EMPTY;
              }

              return loadServerConnectionSecret(result.clientInternalId);
            }),
            catchError(err => {
              Sentry.captureException(err);
              if (store.connectionId() === connectionId) {
                patchState(store, {
                  selectedServerConnectionInternalId: null,
                  selectedServerConnectionDetail: null,
                  loadedServerConnectionId: null,
                  serverConnectionClientInternalIdHint: null,
                });
              }
              return EMPTY;
            }),
            finalize(() => {
              if (store.connectionId() === connectionId) {
                store.setLoading(false);
              }
            }),
          );
        }),
      ),
    ),
    updateServerConnection: rxMethod<{
      clientInternalId: string;
      updateClientDto: UpdateClientDto;
      roleId?: string;
      callback?: () => void;
    }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ clientInternalId, updateClientDto, roleId, callback }) => {
          const detail = store.selectedServerConnectionDetail();
          const serviceAccountUserId = detail?.serviceAccountUser?.id;
          const currentGroupId = detail?.groups?.[0]?.id;

          const updateClient$ = store._keycloakService.keycloakControllerUpdateServiceAccountClient({
            clientInternalId,
            updateClientDto,
          });

          const roleUpdates$: Observable<unknown>[] = [];
          if (roleId && serviceAccountUserId && roleId !== currentGroupId) {
            if (currentGroupId) {
              roleUpdates$.push(
                store._keycloakService.keycloakControllerRemoveUserFromGroup({
                  userId: serviceAccountUserId,
                  groupId: currentGroupId,
                }),
              );
            }
            roleUpdates$.push(
              store._keycloakService.keycloakControllerAddUserToGroup({
                userId: serviceAccountUserId,
                groupId: roleId,
              }),
            );
          }

          const roleUpdate$ = roleUpdates$.length ? forkJoin(roleUpdates$) : of(undefined);

          return forkJoin([updateClient$, roleUpdate$]).pipe(
            map(([updatedClient]) => updatedClient),
            tapResponse({
              next: (updatedClient: ClientRepresentationDto) => {
                const current = store.selectedServerConnectionDetail();
                const updatedRole = roleId ? store.availableRoles().find(role => role.id === roleId) : undefined;
                const connectionId = store.connectionId();
                const connections = connectionId
                  ? store.serverConnections().map(connection =>
                      connection.id === connectionId
                        ? {
                            ...connection,
                            name: updateClientDto.name ?? updatedClient.name ?? connection.name,
                            description: updateClientDto.description ?? connection.description,
                            clientId: updatedClient.clientId ?? updateClientDto.clientId ?? connection.clientId,
                          }
                        : connection,
                    )
                  : store.serverConnections();
                patchState(store, {
                  selectedServerConnectionDetail: current
                    ? {
                        ...current,
                        clientName: updatedClient.clientId ?? current.clientName,
                        client: { ...current.client, ...updatedClient },
                        groups: updatedRole ? [{ id: updatedRole.id, name: updatedRole.name }] : current.groups,
                      }
                    : null,
                  serverConnections: connections,
                  formError: "",
                });
                callback?.();
              },
              error: (err: errorResponse) => {
                Sentry.captureException(err);
                store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                  data: {
                    title: "errorMessages.errorPageTitle",
                    errorMessage: getServerConnectionErrorKey(err, "updateFailed"),
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => store.setLoading(false),
            }),
          );
        }),
      ),
    ),
    deleteServerConnection: rxMethod<{ clientInternalId: string; connectionId: string; shouldNavigate: boolean }>(
      pipe(
        tap(() => store.setLoading(true)),
        exhaustMap(({ clientInternalId, connectionId, shouldNavigate }) =>
          store._keycloakService.keycloakControllerDeleteServiceAccountClient({ clientInternalId }).pipe(
            tapResponse({
              next: () => {
                const connections = store.serverConnections().filter(connection => connection.id !== connectionId);
                patchState(store, {
                  serverConnections: connections,
                  selectedServerConnectionInternalId: null,
                  selectedServerConnectionDetail: null,
                  loadedServerConnectionId: null,
                  serverConnectionClientInternalIdHint: null,
                  pendingNewClientSecret: null,
                  formError: "",
                });
                if (shouldNavigate) {
                  const programId = store._globalStore.selectSignal(FromRouter.selectProgramId)();
                  void store._router.navigate([AppRoutes.root, programId, AppRoutes.admins, AppRoutes.serverConnections]);
                }
              },
              error: (err: errorResponse) => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    regenerateServerConnectionSecret: rxMethod<string>(
      pipe(
        filter((clientInternalId): clientInternalId is string => !!clientInternalId),
        tap(() => store.setLoading(true)),
        exhaustMap(clientInternalId =>
          store._keycloakService.keycloakControllerRegenerateServiceAccountClientSecret({ clientInternalId }).pipe(
            tapResponse({
              next: ({ clientSecret }) => {
                patchState(store, {
                  pendingNewClientSecret: clientSecret ?? null,
                  formError: "",
                });
                if (clientSecret) {
                  store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                    data: {
                      title: "serverConnections.credentials.regenerateSecret.reveal.title",
                      successMessage: "serverConnections.credentials.newSecretNotice",
                      closeText: "buttons.close",
                    },
                  });
                }
              },
              error: (err: errorResponse) => {
                const errorMessage = parseErrorMessage(err);
                Sentry.captureException(err);
                store.setFormError(errorMessage);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
    setPendingNewClientSecret(clientSecret: string | null): void {
      patchState(store, { pendingNewClientSecret: clientSecret });
    },
    clearPendingNewClientSecret(): void {
      patchState(store, { pendingNewClientSecret: null });
    },
    createServerConnection: rxMethod<{
      createClientDto: CreateClientDto;
      description?: string;
      onSuccess?: (response: CreateClientResponseDto) => void;
    }>(
      pipe(
        tap(() => patchState(store, { serverConnectionCreateSubmitting: true })),
        exhaustMap(({ createClientDto, description, onSuccess }) =>
          store._keycloakService.keycloakControllerCreateOrganizationClient({ createClientDto }).pipe(
            switchMap((response: CreateClientResponseDto) => {
              const trimmedDescription = description?.trim();
              const clientInternalId = response.client?.id;
              const clientId = response.client?.clientId;
              if (!trimmedDescription || !clientInternalId || !clientId) {
                return of(response);
              }
              return store._keycloakService
                .keycloakControllerUpdateServiceAccountClient({
                  clientInternalId,
                  updateClientDto: {
                    clientId,
                    description: trimmedDescription,
                  },
                })
                .pipe(map(() => response));
            }),
            tapResponse({
              next: (response: CreateClientResponseDto) => {
                patchState(store, {
                  serverConnectionCreateSubmitting: false,
                  formError: "",
                  serverConnectionsLoaded: false,
                  serverConnections: [],
                });
                onSuccess?.(response);
              },
              error: (err: errorResponse) => {
                patchState(store, { serverConnectionsLoaded: false, serverConnections: [] });
                Sentry.captureException(err);
                store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                  data: {
                    title: "errorMessages.errorPageTitle",
                    errorMessage: getServerConnectionErrorKey(err, "createFailed"),
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => patchState(store, { serverConnectionCreateSubmitting: false }),
            }),
            catchError(() => EMPTY),
          ),
        ),
      ),
    ),
    createInvitations: rxMethod<{ inviteByEmailDto: InviteByEmailDto }>(
      pipe(
        tap(() => patchState(store, { inviteSubmitting: true })),
        exhaustMap(({ inviteByEmailDto }) =>
          store._keycloakService.keycloakControllerCreateInvitations({ inviteByEmailDto }).pipe(
            tapResponse({
              next: () => {
                const programId = store.programId();
                patchState(store, { inviteSubmitting: false });
                void store._router.navigate([AppRoutes.root, programId, AppRoutes.admins, AppRoutes.active]);
              },
              error: err => {
                Sentry.captureException(err);
                store._dialog.open<ConfirmationComponent, ConfirmationDialogData>(ConfirmationComponent, {
                  data: {
                    title: "errorMessages.errorPageTitle",
                    errorMessage: getInviteErrorKey(err, "sendFailed"),
                    closeText: "buttons.close",
                  },
                });
              },
              finalize: () => patchState(store, { inviteSubmitting: false }),
            }),
            catchError(() => EMPTY),
          ),
        ),
      ),
    ),
  })),
  withDevtools(StorageKeys.AdminStore),
  withStorageSync({
    key: `${selectedProgramId}_selfservice_${environment.name}_${StorageKeys.AdminStore}`,
    select: state => ({
      adminsEnabledEntityMap: state.adminsEnabledEntityMap,
      adminsEnabledIds: state.adminsEnabledIds,
      adminsDisabledEntityMap: state.adminsDisabledEntityMap,
      adminsDisabledIds: state.adminsDisabledIds,
      adminRolesEntityMap: state.adminRolesEntityMap,
      adminRolesIds: state.adminRolesIds,
      paginations: state.paginations,
    }),
  }),
);
